import type { Temporal } from 'temporal-polyfill';
import { isNil } from '#layers/base/utils/fn';
import { getTemporalType, isTemporal } from '#layers/base/utils/temporal';

type TemporalType =
	| typeof Temporal.PlainDate
	| typeof Temporal.PlainTime
	| typeof Temporal.PlainDateTime
	| typeof Temporal.ZonedDateTime
	| typeof Temporal.Instant;

type TemporalCompare = (a: unknown, b: unknown) => number;

export function compareNil(a: unknown, b: unknown): number | null {
	if (isNil(a) && isNil(b)) return 0;
	if (!isNil(a) && isNil(b)) return -1;
	if (isNil(a) && !isNil(b)) return 1;
	return null;
}

export function comparePlain(a: unknown, b: unknown): number {
	if (a! < b!) return -1;
	if (a! > b!) return 1;
	return 0;
}

export function compareTemporal(type: TemporalType, a: unknown, b: unknown): number {
	if (a instanceof type && b instanceof type) {
		return (type.compare as TemporalCompare)(a, b);
	}

	return comparePlain(a, b);
}

/**
 * Compares two values for sorting. Returns -1 if a < b, 1 if a > b, 0 if equal.
 * Null/undefined are ordered last for ascending (treated as greater than any value).
 * Temporal values are compared as Temporal objects via their type's compare method.
 *
 * Value-based: probes each value's type at comparison time. Prefer a
 * schema-driven comparator (see `sort-items`) when the field type is known.
 */
export function compareValues(a: unknown, b: unknown): number {
	const nilResult = compareNil(a, b);
	if (nilResult !== null) return nilResult;

	if (isTemporal(a) && isTemporal(b)) {
		const TemporalTypeA = getTemporalType(a);
		const TemporalTypeB = getTemporalType(b);
		if (!TemporalTypeA || TemporalTypeA !== TemporalTypeB) return 0;
		return (TemporalTypeA.compare as TemporalCompare)(a, b);
	}

	return comparePlain(a, b);
}
